import { supabase } from '@/integrations/supabase/client';

export interface TrustScoreData {
	trust_score: number;
	cover_content: number;
	reported: number;
}

/**
 * Calcule et met à jour le trust score d'un créateur
 * @param creatorId - ID du créateur
 * @returns Le nouveau trust score calculé
 */
export async function updateTrustScore(creatorId: string): Promise<number> {
	try {
		const { data, error } = await supabase.rpc('update_trust_score', {
			creator_id: creatorId
		});

		if (error) {
			console.error('Error updating trust score:', error);
			throw error;
		}

		return data;
	} catch (error) {
		console.error('Error updating trust score:', error);
		throw error;
	}
}

/**
 * Updates the content status of a creator
 * @param creatorId - Creator ID
 * @param hasCoverContent - Whether the creator has created content
 * @returns Promise<void>
 */
export async function updateCoverContent(creatorId: string, hasCoverContent: boolean): Promise<void> {
	try {
		// Get current count and increment/decrement
		const { data: currentProfile, error: fetchError } = await supabase
			.from('profiles')
			.select('cover_content')
			.eq('id', creatorId)
			.single();

		if (fetchError) {
			console.error('Error fetching current profile:', fetchError);
			throw fetchError;
		}

		const currentCount = currentProfile?.cover_content || 0;
		const newCount = hasCoverContent ? currentCount + 1 : Math.max(0, currentCount - 1);

		const { error } = await supabase
			.from('profiles')
			.update({ cover_content: newCount })
			.eq('id', creatorId);

		if (error) {
			console.error('Error updating content status:', error);
			throw error;
		}

		// Recalculate trust score after update
		await updateTrustScore(creatorId);
	} catch (error) {
		console.error('Error updating content status:', error);
		throw error;
	}
}

/**
 * Reports a creator
 * @param creatorId - Creator ID
 * @param isReported - Whether the creator is reported
 * @returns Promise<void>
 */
export async function reportCreator(creatorId: string, isReported: boolean): Promise<void> {
	try {
		// Get current count and increment/decrement
		const { data: currentProfile, error: fetchError } = await supabase
			.from('profiles')
			.select('reported')
			.eq('id', creatorId)
			.single();

		if (fetchError) {
			console.error('Error fetching current profile:', fetchError);
			throw fetchError;
		}

		const currentCount = currentProfile?.reported || 0;
		const newCount = isReported ? currentCount + 1 : Math.max(0, currentCount - 1);

		const { error } = await supabase
			.from('profiles')
			.update({ reported: newCount })
			.eq('id', creatorId);

		if (error) {
			console.error('Error reporting creator:', error);
			throw error;
		}

		// Recalculate trust score after update
		await updateTrustScore(creatorId);
	} catch (error) {
		console.error('Error reporting creator:', error);
		throw error;
	}
}

/**
 * Gets trust score data for a creator
 * @param creatorId - Creator ID
 * @returns Trust score data
 */
export async function getTrustScoreData(creatorId: string): Promise<TrustScoreData | null> {
	try {
		const { data, error } = await supabase
			.from('profiles')
			.select('trust_score, cover_content, reported')
			.eq('id', creatorId)
			.single();

		if (error) {
			console.error('Error fetching trust score:', error);
			return null;
		}

		return data;
	} catch (error) {
		console.error('Error fetching trust score:', error);
		return null;
	}
}

/**
 * Updates all creators' trust scores
 * @returns Promise<void>
 */
export async function updateAllTrustScores(): Promise<void> {
	try {
		const { error } = await supabase.rpc('update_all_trust_scores');

		if (error) {
			console.error('Error updating all trust scores:', error);
			throw error;
		}
	} catch (error) {
		console.error('Error updating all trust scores:', error);
		throw error;
	}
}

/**
 * Gets the trust score color based on its value
 * @param score - Trust score (0-200)
 * @returns CSS class for the color
 */
export function getTrustScoreColor(score: number): string {
	if (score >= 150) return 'text-green-600';
	if (score >= 100) return 'text-yellow-600';
	if (score >= 50) return 'text-orange-600';
	return 'text-red-600';
}

/**
 * Gets the trust score label based on its value
 * @param score - Trust score (0-200)
 * @returns Trust level label
 */
export function getTrustScoreLabel(score: number): string {
	if (score >= 150) return 'Excellent';
	if (score >= 100) return 'Good';
	if (score >= 50) return 'Average';
	return 'Poor';
}
